import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FolderArchive, MoreVertical, RotateCcw } from 'lucide-react';
import FoldersTable from './tables/FoldersTable';
import FoldersActionBar from './FoldersActionBar';
import FoldersBreadcrumb from './breadcrumbs/FoldersBreadcrumb';
import SheetFolderCreate from './sheets/SheetFolderCreate';
import SheetFolderEdit from './sheets/SheetFolderEdit';
import SheetFolderFilter from './sheets/SheetFolderFilter';
import SheetPhotoCapture from './sheets/SheetPhotoCapture';
import SheetSelectProcedure from './sheets/SheetSelectProcedure';
import SheetFolderMove from './sheets/SheetFolderMove';
import SheetMandatoryProcedures from './sheets/SheetMandatoryProcedures';
import DialogExportReports from './dialogs/DialogExportReports';
import { useAdminProcedureTemplates } from '@/lib/hooks/use-admin-procedure-templates';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '@/lib/contexts/auth-context';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { DialogMobileApp } from '@/pages/_partials/DialogMobileApp';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { Button } from '@/components/ui/button';
import { getMediaDetailPath } from '@/components/ui/PhotoGallery';
import { BackButton } from '@/components/ui/app/back-button';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const toUserLabel = (user) => {
    if (!user) return '';
    const first = user.first_name ?? user.firstName ?? '';
    const last = user.last_name ?? user.lastName ?? '';
    const full = `${first} ${last}`.trim();
    return full || user.name || user.email || '';
};

const formatDate = (value) => {
    if (!value) return null;
    try {
        const date = new Date(value);
        const dateStr = date.toLocaleDateString('fr-FR');
        const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        return `${dateStr} - ${timeStr}`;
    } catch {
        return null;
    }
};

const resolveAssignedUniqId = (folder, userIndex) => {
    if (!folder) return null;

    const candidate = folder?.user?.uniq_id ?? folder?.user?.id ?? folder?.user_uniq_id ?? folder?.user_id ?? null;

    if (!candidate) return null;
    const asString = String(candidate);

    // If API returns numeric user_id, try mapping to uniq_id using the loaded users.
    if (userIndex?.byNumericId?.[asString]?.uniq_id) {
        return String(userIndex.byNumericId[asString].uniq_id);
    }

    return asString;
};

const mapFolderToItem = (folder, userIndex) => {
    if (!folder) return null;
    const assignedUniqId = resolveAssignedUniqId(folder, userIndex);
    return {
        // Horodaty API uses `uniq_id` as folder identifier in routes.
        id: String(folder.uniq_id ?? folder.id ?? ''),
        type: 'folder',
        name: folder.name ?? i18n.t('folders:dossier'),
        assignedTo: assignedUniqId,
        realizedBy: null,
        createdByName: folder.created_by ?? null,
        createdByEmail: folder.created_by_email ?? null,
        tokenConsumption: folder.token_consumption ?? 0,
        updatedAt: formatDate(folder.updated_at),
        updatedAtRaw: folder.updated_at || null,
        createdAt: formatDate(folder.created_at),
        createdAtRaw: folder.created_at || null,
        reference: folder.ref ?? folder.external_id ?? folder.external_ref ?? '',
        user: assignedUniqId,
        interventionDate: folder.intervention_date ?? null,
        address: [folder.street_1, folder.cp, folder.city].filter(Boolean).join(' ').trim() || '',
        comments: folder.notes ?? '',
        // Propriétés de verrouillage/galerie
        isLocked: Boolean(folder.is_locked),
        isGalleryFolder: Boolean(folder.is_folder_gallery ?? folder.is_gallery_folder),
        isArchived: Boolean(folder.is_archived),
        archivedChildrenCount: folder.archived_children_count ?? 0,
        status: folder.status ?? null,
        companyName: folder.company_name ?? null,
        raw: folder,
    };
};

const mapInterventionToItem = (intervention) => {
    if (!intervention) return null;
    const isProcedureReport = intervention.type === 'procedure' || intervention.type === 'PROCEDURE';
    return {
        id: String(intervention.uniq_id ?? intervention.id ?? ''),
        type: isProcedureReport ? 'procedure_report' : 'intervention',
        name: intervention.name ?? i18n.t('folders:intervention'),
        assignedTo: intervention.user_uniq_id ?? null,
        realizedBy: intervention.owned_by ?? intervention.created_by ?? null,
        createdByName: intervention.created_by ?? null,
        createdByEmail: intervention.created_by_email ?? null,
        tokenConsumption: intervention.token_consumption ?? 0,
        updatedAt: formatDate(intervention.updated_at),
        updatedAtRaw: intervention.updated_at || null,
        createdAt: formatDate(intervention.created_at),
        createdAtRaw: intervention.created_at || null,
        reference: intervention.ref ?? '',
        procedureTemplateId: intervention.procedure_template_uniq_id ?? null,
        status: isProcedureReport ? (intervention.procedure_status ?? null) : (intervention.status ?? null),
        statusLabel:
            intervention.procedure_status === 'completed'
                ? i18n.t('folders:finalise')
                : intervention.procedure_status === 'draft'
                  ? i18n.t('folders:brouillon')
                  : null,
        isArchived: Boolean(intervention.is_archived),
        raw: intervention,
    };
};

// Mapper le mime type vers une extension de fichier
const getExtensionFromMime = (mime) => {
    const mimeToExt = {
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'image/heic': '.heic',
        'image/heif': '.heif',
    };
    return mimeToExt[mime] || '.jpg';
};

// Base URL du gateway pour les URLs d'images
const GATEWAY_BASE_URL = import.meta.env.VITE_GATEWAY_BASE_URL ?? '/api/gateway';

// Préfixer une URL relative avec le gateway
const toGatewayUrl = (url) => {
    if (!url) return null;
    // Si l'URL commence par /api/, la transformer en URL gateway
    if (url.startsWith('/api/')) {
        return GATEWAY_BASE_URL + url.substring(4); // Enlever /api et ajouter le gateway
    }
    return url;
};

// Ajouter l'extension au nom si elle n'est pas déjà présente
const addExtensionToName = (name, mime) => {
    if (!name) return 'Photo.jpg';
    // Vérifier si le nom a déjà une extension image
    if (/\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(name)) {
        return name;
    }
    return name + getExtensionFromMime(mime);
};

const mapPictureToItem = (doc, thumbnailUrl) => {
    if (!doc) return null;
    const id = String(doc.uniq_id ?? doc.id ?? '');
    // Récupérer le certificateId depuis les différentes sources possibles
    const certificateId = doc.certificate_uniq_id ?? doc.certificate?.uniq_id ?? null;
    // Privilégier file_name (avec extension) sur name
    const baseName = doc.file_name ?? doc.name ?? i18n.t('folders:photo');
    const nameWithExt = addExtensionToName(baseName, doc.mime);
    // Récupérer le statut de certification
    const certificationStatus = doc.status ?? null;
    return {
        id,
        type: 'photo',
        name: nameWithExt,
        assignedTo: null,
        realizedBy: null,
        createdByName: doc.created_by || null,
        updatedAt: formatDate(doc.updated_at),
        updatedAtRaw: doc.updated_at || null,
        createdAt: formatDate(doc.created_at),
        createdAtRaw: doc.created_at || null,
        reference: doc.file_name ?? '',
        thumbnailUrl,
        certificateId,
        tokenConsumption: certificateId ? 1 : 0,
        mime: doc.mime,
        latitude: doc.latitude ?? null,
        longitude: doc.longitude ?? null,
        certificationStatus,
        isArchived: Boolean(doc.is_archived),
        raw: doc,
    };
};

export default function FoldersExplorer({ searchQuery = '' }) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { user: authUser, isLoading: isAuthLoading, isOwnerOrAdmin } = useAuth();
    const { getCache, setCache, invalidateCache, clearReportReturnTo, setReportReturnTo } = useApiCache();
    const { can, canOnDescendants, isSuperAdmin } = usePermissions();

    // Admin templates map for badge display
    const { templates: adminTemplates, companyName: adminCompanyName } = useAdminProcedureTemplates();
    const adminTemplateMap = useMemo(() => {
        const map = new Map();
        for (const t of adminTemplates) {
            map.set(t.uniq_id, t.creator?.company || adminCompanyName || t('folders:admin'));
        }
        return map;
    }, [adminTemplates, adminCompanyName, t]);

    // Réinitialiser le returnTo des rapports quand on arrive sur la page Folders
    useEffect(() => {
        clearReportReturnTo();
    }, [clearReportReturnTo]);

    const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
    const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
    const [folderActionDialog, setFolderActionDialog] = useState(null); // { type: 'archive' | 'restore', item }
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isMobileAppModalOpen, setIsMobileAppModalOpen] = useState(false);
    const [isPhotoCaptureOpen, setIsPhotoCaptureOpen] = useState(false);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);
    const [isProcedurePanelOpen, setIsProcedurePanelOpen] = useState(false);
    const [isMovePanelOpen, setIsMovePanelOpen] = useState(false);
    const [isMandatoryProceduresPanelOpen, setIsMandatoryProceduresPanelOpen] = useState(false);
    const [isExportReportsOpen, setIsExportReportsOpen] = useState(false);
    const [currentMandatoryProcedures, setCurrentMandatoryProcedures] = useState([]);
    const [itemToMove, setItemToMove] = useState(null);
    const [isMoveSubmitting, setIsMoveSubmitting] = useState(false);
    const [isCreateSubmitting, setIsCreateSubmitting] = useState(false);
    const [isEditSubmitting, setIsEditSubmitting] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [activeFilters, setActiveFilters] = useState(null);
    const [resetKey, setResetKey] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingPhotos, setIsLoadingPhotos] = useState(false); // Loader spécifique pour les photos
    const [assigningFolderId, setAssigningFolderId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [subsidiaries, setSubsidiaries] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);

    // Sorting state (synced with DataTable via manualSorting)
    const [sorting, setSorting] = useState([{ id: 'updatedAt', desc: true }]);

    // Tracker les dossiers déjà chargés (avec leur refreshKey) pour éviter les rechargements inutiles
    const loadedFoldersRef = useRef(new Map());
    // Mode archives dérivé de l'URL : /folders/archived/... → mode archives
    const isArchiveRoute = location.pathname.startsWith('/folders/archived');
    // Helper pour construire les URLs de dossiers avec le bon préfixe
    const folderUrl = useCallback(
        (id) => {
            return isArchiveRoute ? `/folders/archived/${id}` : `/folders/${id}`;
        },
        [isArchiveRoute],
    );

    // Récupérer les données du cache global ou initialiser avec les valeurs par défaut
    const [itemsByFolderId, setItemsByFolderIdLocal] = useState(
        () => getCache('folders:items', 'folders') || { root: [] },
    );
    const [folderMetadata, setFolderMetadataLocal] = useState(() => getCache('folders:metadata', 'folders') || {});
    const [pictureThumbsById, setPictureThumbsByIdLocal] = useState(() => getCache('folders:thumbs', 'folders') || {});
    const [primaryInterventionByFolderId, setPrimaryInterventionByFolderIdLocal] = useState(
        () => getCache('folders:primaryIntervention', 'folders') || {},
    );
    const [collaborators, setCollaboratorsLocal] = useState(() => getCache('collaborators', 'collaborators') || []);

    // Wrappers pour mettre à jour l'état local ET le cache global
    const setItemsByFolderId = useCallback(
        (updater) => {
            setItemsByFolderIdLocal((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                setCache('folders:items', next, 'folders');
                return next;
            });
        },
        [setCache],
    );

    const setFolderMetadata = useCallback(
        (updater) => {
            setFolderMetadataLocal((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                setCache('folders:metadata', next, 'folders');
                return next;
            });
        },
        [setCache],
    );

    const setPictureThumbsById = useCallback(
        (updater) => {
            setPictureThumbsByIdLocal((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                setCache('folders:thumbs', next, 'folders');
                return next;
            });
        },
        [setCache],
    );

    const setPrimaryInterventionByFolderId = useCallback(
        (updater) => {
            setPrimaryInterventionByFolderIdLocal((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                setCache('folders:primaryIntervention', next, 'folders');
                return next;
            });
        },
        [setCache],
    );

    const setCollaborators = useCallback(
        (updater) => {
            setCollaboratorsLocal((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                setCache('collaborators', next, 'collaborators');
                return next;
            });
        },
        [setCache],
    );

    // Fonction pour invalider le cache après une mutation
    const invalidateFoldersCache = useCallback(() => {
        invalidateCache('folders');
    }, [invalidateCache]);

    const userIndex = useMemo(() => {
        const byNumericId = {};
        const byUniqId = {};
        for (const u of collaborators) {
            const uniq = u?.uniq_id ?? u?.uniqId ?? u?.id;
            const numeric = u?.id;

            if (numeric != null) {
                byNumericId[String(numeric)] = u;
            }
            if (uniq != null) {
                byUniqId[String(uniq)] = u;
            }
        }
        return { byNumericId, byUniqId };
    }, [collaborators]);

    const collaboratorOptions = useMemo(() => {
        const options = [];
        const seenIds = new Set();
        for (const u of collaborators) {
            const uniq = u?.uniq_id ?? u?.uniqId ?? null;
            if (!uniq || seenIds.has(uniq)) continue;
            seenIds.add(uniq);
            options.push({
                id: String(uniq),
                name: toUserLabel(u) || String(uniq),
                email: u?.email ?? null,
                companyName: u?.company_name ?? null,
            });
        }
        return options;
    }, [collaborators]);

    // Parse current folder ID from URL
    // URL format: /folders → root, /folders/archived → archived root, /folders/{id} → folder, /folders/archived/{id} → archived folder
    const currentId = useMemo(() => {
        let path = location.pathname.replace('/folders', '');
        // Retirer le segment 'archived' pour isoler l'ID
        if (path.startsWith('/archived')) {
            path = path.replace('/archived', '');
        }
        const pathParts = path.split('/').filter((segment) => segment && segment !== 'interventions');
        if (pathParts.length > 0) return pathParts[pathParts.length - 1];
        return isArchiveRoute ? 'archived' : 'root';
    }, [location.pathname, isArchiveRoute]);

    // État pour stocker les informations de hiérarchie résolues dynamiquement
    const [resolvedHierarchy, setResolvedHierarchy] = useState({
        rootFolderId: null, // ID du dossier racine
        isSubfolder: false, // true si currentId est un sous-dossier
        parentChain: [], // Chaîne des parents pour le breadcrumb [{id, name}]
    });
    const hierarchyLoadingRef = useRef(false);

    // Pour compatibilité avec le code existant qui utilise currentPath
    const currentPath = useMemo(() => {
        if (currentId === 'root' || currentId === 'archived') return [];
        if (resolvedHierarchy.isSubfolder && resolvedHierarchy.rootFolderId) {
            // Pour un sous-dossier, on a besoin du rootFolderId et du currentId pour les appels API
            return [resolvedHierarchy.rootFolderId, currentId];
        }
        // Dossier racine
        return [currentId];
    }, [currentId, resolvedHierarchy]);

    // Get current folder id (last segment of path, or 'root')
    const currentFolderId = currentId;

    // Calculate depth (0 = root, 1 = folder, 2+ = subfolder)
    const depth = currentPath.length;

    // Vérifier si on est dans l'intervention galerie (Photos non classées)
    const isInsideGalleryIntervention = useMemo(() => {
        return Object.values(primaryInterventionByFolderId).includes(String(currentFolderId));
    }, [primaryInterventionByFolderId, currentFolderId]);

    // Get breadcrumb data from resolved hierarchy
    const breadcrumbItems = useMemo(() => {
        if (currentId === 'root' || currentId === 'archived') return [];

        // Utiliser la chaîne résolue si disponible
        if (resolvedHierarchy.parentChain.length > 0) {
            return resolvedHierarchy.parentChain;
        }

        // Fallback sur les métadonnées en cache
        if (currentPath.length > 0) {
            return currentPath.map((id) => ({
                id,
                name: folderMetadata[id]?.name || '…',
            }));
        }

        return [];
    }, [currentId, resolvedHierarchy.parentChain, currentPath, folderMetadata]);

    // Note: Les noms du breadcrumb sont désormais récupérés via ancestors dans resolveHierarchy

    // Get current items based on path
    const currentItems = useMemo(() => {
        return itemsByFolderId[currentFolderId] || [];
    }, [itemsByFolderId, currentFolderId]);

    // Résoudre le dossier courant (depth=1) depuis le cache root ou archived
    const currentFolderItem = useMemo(() => {
        if (depth !== 1) return null;
        const all = [...(itemsByFolderId.root || []), ...(itemsByFolderId.archived || [])];
        return all.find((item) => item.id === currentFolderId) ?? null;
    }, [depth, currentFolderId, itemsByFolderId]);

    // Résoudre l'élément courant (dossier ou sous-dossier) pour le menu header
    const currentDirectoryItem = useMemo(() => {
        if (depth === 0) return null;
        // depth === 1 : utiliser currentFolderItem qui vient du cache root/archived
        if (depth === 1) return currentFolderItem;
        // depth >= 2 : construire depuis les métadonnées du dossier courant
        const meta = folderMetadata[currentFolderId];
        if (!meta) return null;
        return {
            id: currentFolderId,
            name: meta.name,
            isArchived: Boolean(meta.isArchived),
            isLocked: Boolean(meta.isLocked),
            isGalleryFolder: Boolean(meta.isGalleryFolder),
        };
    }, [depth, currentFolderId, currentFolderItem, folderMetadata]);

    const refreshRoot = async (filters = activeFilters, sortOverride = sorting) => {
        // Guard : ne pas charger le root si on est dans un dossier/sous-dossier
        if (currentFolderId !== 'root') {
            console.log('[FoldersExplorer] refreshRoot SKIPPED (not at root)', { currentFolderId });
            return;
        }
        setIsLoading(true);
        setErrorMessage('');
        try {
            const periodDays = filters?.period && filters.period !== 'all' ? Number(filters.period) : null;
            const hasPeriod = periodDays && !Number.isNaN(periodDays) && periodDays > 0;

            const collaboratorId =
                filters?.collaborator && filters.collaborator !== 'all' ? filters.collaborator : null;

            // --- Build company_ids for V2 endpoint ---
            const subsidiaryIds = filters?.subsidiaries;
            const hasSubsidiaryFilter = Array.isArray(subsidiaryIds) && subsidiaryIds.length > 0;

            // Build V2 params
            const apiParams = {};
            if (hasSubsidiaryFilter) {
                apiParams.company_ids = subsidiaryIds;
            }
            if (hasPeriod) apiParams.days = periodDays;
            if (collaboratorId) {
                apiParams.users = Array.isArray(collaboratorId) ? collaboratorId : [collaboratorId];
            }

            // Sorting params
            const sortColumnMap = { name: 'name', updatedAt: 'updated_at', createdAt: 'created_at' };
            if (sortOverride?.length > 0) {
                const col = sortOverride[0];
                const apiCol = sortColumnMap[col.id];
                if (apiCol) {
                    apiParams.sort_by = apiCol;
                    apiParams.sort_direction = col.desc ? 'desc' : 'asc';
                }
            }

            console.log('[FoldersExplorer] refreshRoot → API params', apiParams);
            const res = await horodatyApi.getDocumentTreeDirectoryContent('root', apiParams);
            const folders = res?.data?.children ?? [];
            console.log('[FoldersExplorer] refreshRoot → résultat', { count: folders.length, folders });
            const mapped = (Array.isArray(folders) ? folders : [])
                .map((f) => mapFolderToItem(f, userIndex))
                .filter(Boolean);

            setItemsByFolderId((prev) => ({ ...prev, root: mapped }));
            setFolderMetadata((prev) => {
                const next = { ...prev };
                for (const folder of mapped) {
                    next[folder.id] = { name: folder.name };
                }
                return next;
            });
        } catch (error) {
            const message = error?.response?.data?.message || t('folders:impossibleDeChargerLesDossiers');
            setErrorMessage(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const refreshArchived = async (filters = activeFilters, sortOverride = sorting) => {
        console.log('[FoldersExplorer] refreshArchived called', { filters });
        setIsLoading(true);
        setErrorMessage('');
        try {
            const apiParams = {};
            const subsidiaryIds = filters?.subsidiaries;
            if (Array.isArray(subsidiaryIds) && subsidiaryIds.length > 0) {
                apiParams.company_ids = subsidiaryIds;
            }
            const collaboratorId =
                filters?.collaborator && filters.collaborator !== 'all' ? filters.collaborator : null;
            if (collaboratorId) {
                apiParams.users = Array.isArray(collaboratorId) ? collaboratorId : [collaboratorId];
            }
            const search = filters?.search;
            if (search) apiParams.search = search;

            // Sorting params
            const sortColumnMap = { name: 'name', updatedAt: 'updated_at', createdAt: 'created_at' };
            if (sortOverride?.length > 0) {
                const col = sortOverride[0];
                const apiCol = sortColumnMap[col.id];
                if (apiCol) {
                    apiParams.sort_by = apiCol;
                    apiParams.sort_direction = col.desc ? 'desc' : 'asc';
                }
            }

            const res = await horodatyApi.getDocumentTreeDirectoryContent('archived', apiParams);
            const folders = res?.data?.children ?? [];
            const mapped = (Array.isArray(folders) ? folders : [])
                .map((f) => mapFolderToItem(f, userIndex))
                .filter(Boolean);

            setItemsByFolderId((prev) => ({ ...prev, archived: mapped }));
        } catch (error) {
            const message = error?.response?.data?.message || t('folders:impossibleDeChargerLesArchives');
            setErrorMessage(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Wait for auth to finish loading before fetching collaborators
        if (isAuthLoading) return;

        // Load collaborators (best-effort) for assignment + filters.
        // Prefer non-manager routes: GET /api/user -> company_id -> GET /api/companies/{id}/users
        let isMounted = true;

        (async () => {
            try {
                const companyId = authUser?.company_uniq_id ?? authUser?.company?.uniq_id ?? null;
                if (!companyId) return;

                const companyUsersRes = await horodatyApi.getCompanyUsers(companyId);
                const users = companyUsersRes?.data?.data ?? [];
                const list = Array.isArray(users) ? users : [];
                if (!isMounted) return;
                setCollaborators(list);

                // Load subsidiaries if user has DESCENDANTS permission on LIST_FOLDERS
                if (canOnDescendants('LIST_FOLDERS')) {
                    try {
                        const subsRes = await horodatyApi.subsidiaries.list(companyId);
                        const subs = subsRes?.data?.data ?? subsRes?.data ?? [];
                        if (isMounted) setSubsidiaries(Array.isArray(subs) ? subs : []);
                    } catch {
                        // Subsidiaries unavailable — keep empty
                    }
                }
            } catch {
                // If we can't resolve company users, keep empty list.
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [isAuthLoading, authUser]);

    // Note: On ne révoque plus les URLs blob à chaque changement de dossier
    // car elles sont valides tant que la page n'est pas rechargée
    // et on veut les garder en cache pour une navigation instantanée

    // Résoudre la hiérarchie pour un ID donné (dossier racine ou sous-dossier)
    // La résolution de la hiérarchie est maintenant faite via l'endpoint V2 getContent
    // qui retourne les ancestors directement. Pas besoin de pré-charger.
    // Le breadcrumb sera mis à jour après le chargement du contenu.

    useEffect(() => {
        // Réinitialiser la hiérarchie pour éviter d'afficher le breadcrumb du dossier précédent
        setResolvedHierarchy({
            rootFolderId: null,
            isSubfolder: false,
            parentChain: [],
        });

        // Root list
        if (currentFolderId === 'root') {
            refreshRoot();
            return;
        }

        // Archives list
        if (currentFolderId === 'archived') {
            refreshArchived();
            return;
        }

        // Si on a déjà les métadonnées avec parentChain en cache, mettre à jour immédiatement
        const cached = folderMetadata[currentFolderId];
        if (cached?.rootFolderId && cached?.parentChain) {
            setResolvedHierarchy({
                rootFolderId: cached.rootFolderId,
                isSubfolder: cached.rootFolderId !== currentFolderId,
                parentChain: cached.parentChain,
            });
        }

        let isMounted = true;
        setErrorMessage('');

        // VÉRIFIER LE CACHE: Si on a déjà chargé ce dossier avec le même refreshKey, le même mode archive
        // ET la même langue, pas besoin de recharger (l'API traduit les noms par défaut selon la langue)
        const archiveMode = isArchiveRoute ? 'archived' : 'normal';
        const cacheKey = `${currentFolderId}::${archiveMode}`;
        const lastLoaded = loadedFoldersRef.current.get(cacheKey);
        const alreadyLoaded = lastLoaded === `${refreshKey}::${i18n.language}`;
        const cachedItems = itemsByFolderId[currentFolderId] || [];
        const hasPhotosInCache = cachedItems.some((it) => it.type === 'photo');

        // Si on a déjà chargé ce dossier avec le même refreshKey, utiliser le cache
        if (alreadyLoaded) {
            // Restaurer les procédures obligatoires depuis le cache des métadonnées
            const cachedMeta = folderMetadata[currentFolderId];
            if (cachedMeta?.mandatoryProcedures) {
                setCurrentMandatoryProcedures(cachedMeta.mandatoryProcedures);
            }
            setIsLoading(false);
            return;
        }

        // Charger via l'endpoint unifié V2 getContent
        setIsLoading(true);
        if (!hasPhotosInCache) {
            setIsLoadingPhotos(true);
        }

        (async () => {
            try {
                // === UTILISER L'ENDPOINT UNIFIÉ V2 getContent ===
                // Propager TOUS les filtres (période, subsidiaires, collaborateurs)
                const apiParams = { pictures_limit: 30 };
                if (activeFilters) {
                    const periodDays =
                        activeFilters.period && activeFilters.period !== 'all' ? Number(activeFilters.period) : null;
                    if (periodDays && !Number.isNaN(periodDays) && periodDays > 0) {
                        apiParams.days = periodDays;
                    }
                    // Filtre subsidiaires
                    const subsidiaryIds = activeFilters.subsidiaries;
                    if (Array.isArray(subsidiaryIds) && subsidiaryIds.length > 0) {
                        apiParams.company_ids = subsidiaryIds;
                    }
                    // Filtre collaborateurs
                    const collaboratorId =
                        activeFilters.collaborator && activeFilters.collaborator !== 'all'
                            ? activeFilters.collaborator
                            : null;
                    if (collaboratorId) {
                        apiParams.users = Array.isArray(collaboratorId) ? collaboratorId : [collaboratorId];
                    }
                }

                // En mode archives, inclure les éléments archivés
                if (isArchiveRoute) {
                    apiParams.include_archived = 1;
                }

                console.log('[FoldersExplorer] loadFolder', { currentFolderId, apiParams, activeFilters });
                const res = await horodatyApi.getDocumentTreeDirectoryContent(currentFolderId, apiParams);
                const { type, item, ancestors, gallery_intervention_id, children, pictures, pictures_total } = res.data;

                if (!isMounted) return;

                // Mettre à jour les métadonnées et le breadcrumb depuis la réponse
                const isFolder = type === 'folder';
                const rootFolderId = isFolder ? item.uniq_id : item.folder_uniq_id;

                // Construire la chaîne des parents depuis ancestors
                const parentChain = ancestors.map((ancestor) => ({
                    id: ancestor.uniq_id,
                    name: ancestor.name,
                    isRoot: ancestor.type === 'folder',
                }));

                // Mettre à jour la hiérarchie résolue
                setResolvedHierarchy({
                    rootFolderId,
                    isSubfolder: !isFolder,
                    parentChain,
                });

                // Stocker les procédures obligatoires du dossier/sous-dossier courant
                setCurrentMandatoryProcedures(item.mandatory_procedures ?? []);

                // Mettre à jour les métadonnées du cache
                setFolderMetadata((prev) => ({
                    ...prev,
                    [currentFolderId]: {
                        name: item.name,
                        rootFolderId,
                        parentChain,
                        mandatoryProcedures: item.mandatory_procedures ?? [],
                        isArchived: Boolean(item.is_archived),
                        isLocked: Boolean(item.is_locked),
                        isGalleryFolder: Boolean(item.is_gallery_folder),
                    },
                }));

                // Sauvegarder l'ID de l'intervention galerie si c'est un dossier
                if (gallery_intervention_id) {
                    setPrimaryInterventionByFolderId((prev) => ({
                        ...prev,
                        [String(currentFolderId)]: gallery_intervention_id,
                    }));
                }

                // Mapper les enfants (sous-dossiers ou sous-sous-dossiers)
                // Exclure la gallery intervention (conteneur technique pour les photos du dossier)
                const mappedChildren = children
                    .filter((sf) => String(sf.uniq_id) !== gallery_intervention_id)
                    .map((sf) => {
                        const isGalleryIntervention =
                            sf.type === 'folder_gallery' ||
                            (gallery_intervention_id && String(sf.uniq_id) === gallery_intervention_id);
                        return {
                            id: String(sf.uniq_id),
                            type:
                                sf.type === 'procedure' || sf.type === 'PROCEDURE'
                                    ? 'procedure_report'
                                    : 'intervention',
                            name: sf.name ?? t('folders:intervention'),
                            assignedTo: sf.user_uniq_id ?? null,
                            realizedBy: sf.owned_by ?? sf.created_by ?? null,
                            createdByName: sf.created_by ?? null,
                            tokenConsumption: sf.token_consumption ?? 0,
                            updatedAt: formatDate(sf.updated_at),
                            updatedAtRaw: sf.updated_at || null,
                            createdAt: formatDate(sf.created_at),
                            createdAtRaw: sf.created_at || null,
                            reference: sf.ref ?? '',
                            procedureTemplateId: sf.procedure_template_uniq_id ?? null,
                            status:
                                sf.type === 'procedure' || sf.type === 'PROCEDURE'
                                    ? (sf.procedure_status ?? null)
                                    : (sf.status ?? null),
                            statusLabel:
                                sf.procedure_status === 'completed'
                                    ? t('folders:finalise')
                                    : sf.procedure_status === 'draft'
                                      ? t('folders:brouillon')
                                      : null,
                            isLocked: isGalleryIntervention,
                            isGalleryIntervention,
                            isArchived: Boolean(sf.is_archived),
                            archivedChildrenCount: sf.archived_children_count ?? 0,
                            raw: sf,
                        };
                    });

                // Mapper les photos avec URLs (le navigateur gère le cache HTTP)
                const photoItems = pictures.map((pic) => {
                    const id = String(pic.uniq_id);
                    const baseName = pic.file_name ?? pic.name ?? t('folders:photo');
                    const nameWithExt = addExtensionToName(baseName, pic.mime);
                    return {
                        id,
                        type: 'photo',
                        name: nameWithExt,
                        assignedTo: null,
                        realizedBy: null,
                        createdByName: pic.created_by || null,
                        updatedAt: formatDate(pic.updated_at),
                        updatedAtRaw: pic.updated_at || null,
                        createdAt: formatDate(pic.created_at),
                        createdAtRaw: pic.created_at || null,
                        reference: pic.file_name ?? '',
                        thumbnailUrl: toGatewayUrl(pic.thumbnail_url),
                        imageUrl: toGatewayUrl(pic.image_url),
                        certificateId: pic.certificate_uniq_id ?? null,
                        tokenConsumption: pic.certificate_uniq_id ? 1 : 0,
                        mime: pic.mime,
                        latitude: pic.latitude ?? null,
                        longitude: pic.longitude ?? null,
                        certificationStatus: pic.certification_status ?? null,
                        isArchived: Boolean(pic.is_archived),
                        raw: pic,
                    };
                });

                const combined = [...mappedChildren, ...photoItems];
                setItemsByFolderId((prev) => ({ ...prev, [currentFolderId]: combined }));
                loadedFoldersRef.current.set(
                    `${currentFolderId}::${isArchiveRoute ? 'archived' : 'normal'}`,
                    `${refreshKey}::${i18n.language}`,
                );
                setIsLoading(false);
                setIsLoadingPhotos(false);
            } catch (error) {
                const message = error?.response?.data?.message || t('folders:impossibleDeChargerCeDossier');
                if (!isMounted) return;
                setErrorMessage(message);
                toast.error(message);
                setIsLoading(false);
                setIsLoadingPhotos(false);
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [currentFolderId, refreshKey, isArchiveRoute, t]);

    // Filter items based on search and archive browsing mode
    const isIndividual = authUser?.role === 'user';

    const filteredItems = useMemo(() => {
        let items = currentItems;

        if (depth >= 1) {
            if (isArchiveRoute) {
                // Mode archives : ne montrer que les éléments archivés
                // ou les éléments non-archivés qui mènent vers du contenu archivé
                items = items.filter(
                    (item) =>
                        item.isArchived ||
                        (item.archivedChildrenCount && item.archivedChildrenCount > 0) ||
                        item.type === 'photo', // les photos archivées sont déjà filtrées par l'API
                );
            } else {
                // Mode normal : masquer tous les éléments archivés
                items = items.filter((item) => !item.isArchived);
            }
        }

        // Masquer les rapports de procédures pour les Particuliers
        if (isIndividual) {
            items = items.filter((item) => item.type !== 'procedure_report');
        }

        if (!searchQuery) return items;
        return items.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [currentItems, searchQuery, depth, isArchiveRoute, isIndividual]);

    // Navigation handlers - URLs avec préfixe archive si nécessaire
    const handleNavigateToFolder = (targetId) => {
        navigate(folderUrl(targetId));
    };

    const handleOpenPhoto = (item) => {
        // Accepte soit un objet item complet (avec `mime`), soit un id en fallback.
        const doc = typeof item === 'object' && item !== null ? { uniq_id: item.id, ...item } : { uniq_id: item };
        const path = getMediaDetailPath(doc);
        if (!path) return;
        navigate(path, { state: { from: location.pathname } });
    };

    // Télécharger une photo
    const handleDownloadPhoto = async (item) => {
        const folderId = currentPath[0];
        const interventionId =
            currentPath.length >= 2 ? currentPath[currentPath.length - 1] : primaryInterventionByFolderId[folderId];
        const pictureId = item.id;

        if (!folderId || !interventionId || !pictureId) {
            toast.error(t('folders:impossibleDeTelechargerLaPhoto'));
            return;
        }

        try {
            const response = await horodatyApi.getInterventionPicture(folderId, interventionId, pictureId, {
                format: 'original',
                band: false,
            });

            // Déterminer l'extension à partir du content-type
            const contentType = response.headers['content-type'] || 'image/jpeg';
            const extensionMap = {
                'image/jpeg': '.jpg',
                'image/png': '.png',
                'image/gif': '.gif',
                'image/webp': '.webp',
                'image/heic': '.heic',
                'image/heif': '.heif',
            };
            const extension = extensionMap[contentType] || '.jpg';

            // Construire le nom du fichier avec extension
            let fileName = item.name || item.reference || `photo-${pictureId}`;
            // Ajouter l'extension si elle n'est pas déjà présente
            if (!fileName.match(/\.(jpg|jpeg|png|gif|webp|heic|heif)$/i)) {
                fileName += extension;
            }

            // Créer un lien de téléchargement
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('folders:photoTelechargee'));
        } catch (error) {
            console.error('[handleDownloadPhoto] Error:', error);
            toast.error(t('folders:impossibleDeTelechargerLaPhoto'));
        }
    };

    // Voir le certificat d'une photo
    const handleViewCertificate = async (item) => {
        const folderId = currentPath[0];
        const interventionId =
            currentPath.length >= 2 ? currentPath[currentPath.length - 1] : primaryInterventionByFolderId[folderId];
        const pictureId = item.id;
        const certificateId = item.certificateId || item.raw?.certificate_uniq_id || item.raw?.certificate?.uniq_id;

        if (!folderId || !interventionId || !pictureId || !certificateId) {
            toast.error(t('folders:certificatNonDisponible'));
            return;
        }

        try {
            const response = await horodatyApi.getInterventionPictureCertificate(
                folderId,
                interventionId,
                pictureId,
                certificateId,
            );

            // Ouvrir le PDF dans un nouvel onglet
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error('[handleViewCertificate] Error:', error);
            toast.error(t('folders:impossibleDAfficherLeCertificat'));
        }
    };

    // Archiver une photo
    const handleArchivePhoto = async (item) => {
        if (!item?.id) return;
        try {
            await horodatyApi.archivePicture(item.id);
            toast.success(t('folders:photoArchiveeAvecSucces'));
            setRefreshKey((k) => k + 1);
        } catch (error) {
            toast.error(error?.response?.data?.message || t('folders:impossibleDArchiverLaPhoto'));
        }
    };

    const handleBreadcrumbNavigate = (index) => {
        if (index === -1) {
            navigate(isArchiveRoute ? '/folders/archived' : '/folders');
        } else if (breadcrumbItems[index]) {
            navigate(folderUrl(breadcrumbItems[index].id));
        }
    };

    // Action handlers
    const handleCreateFolder = (folderData) => {
        const name = String(folderData?.name ?? '').trim();
        if (!name) return;

        setIsLoading(true);
        setIsCreateSubmitting(true);
        setErrorMessage('');

        // Root: create a folder
        if (depth === 0) {
            horodatyApi
                .createFolder({
                    nom: name,
                    external_ref: folderData.reference || undefined,
                    notes: folderData.comments || undefined,
                    rue: folderData.address || undefined,
                    'date-intervention': folderData.interventionDate || undefined,
                    'user-to-affect': folderData.user || undefined,
                    status: folderData.status || undefined,
                    mandatory_procedures: folderData.procedures?.length ? folderData.procedures : undefined,
                })
                .then(() => {
                    refreshRoot();
                    toast.success(t('folders:dossierCree'));
                })
                .catch((error) => {
                    const message = error?.response?.data?.message || t('folders:impossibleDeCreerLeDossier');
                    setErrorMessage(message);
                    toast.error(message);
                })
                .finally(() => {
                    setIsCreatePanelOpen(false);
                    setResetKey((prev) => prev + 1);
                    setIsLoading(false);
                    setIsCreateSubmitting(false);
                });

            return;
        }

        // Inside a folder: "sub-folder" creation actually creates an intervention (ROUTES.md)
        const folderId = currentPath?.[0];
        if (!folderId) {
            setErrorMessage("Impossible de créer l'intervention.");
            setIsCreatePanelOpen(false);
            setResetKey((prev) => prev + 1);
            setIsLoading(false);
            setIsCreateSubmitting(false);
            return;
        }

        // Si on est dans un sous-dossier (depth >= 2), on passe le parent_subfolder_id
        const parentSubfolderId = depth >= 2 ? currentPath[currentPath.length - 1] : undefined;

        horodatyApi
            .createIntervention(folderId, {
                name,
                parent_subfolder_id: parentSubfolderId,
                user_uniq_id: folderData.user || undefined,
                status: folderData.status || undefined,
                mandatory_procedures: folderData.procedures?.length ? folderData.procedures : undefined,
            })
            .then((res) => {
                const created = res?.data?.data;
                const interventionId = String(created?.uniq_id ?? created?.id ?? '').trim();
                if (interventionId) {
                    setPrimaryInterventionByFolderId((prev) => ({
                        ...prev,
                        [String(folderId)]: interventionId,
                    }));
                }
                // Rafraîchir le dossier courant pour voir le nouveau sous-dossier dans la liste
                loadedFoldersRef.current.clear();
                setRefreshKey((k) => k + 1);
                toast.success(t('folders:sousDossierCree'));
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('folders:impossibleDeCreerLIntervention');
                setErrorMessage(message);
                toast.error(message);
            })
            .finally(() => {
                setIsCreatePanelOpen(false);
                setResetKey((prev) => prev + 1);
                setIsLoading(false);
                setIsCreateSubmitting(false);
            });
    };

    const handleUpdateItem = (itemData) => {
        if (!itemData?.id) {
            setIsEditPanelOpen(false);
            setSelectedItem(null);
            return;
        }

        // Vérifier si le dossier est verrouillé ou galerie avant de mettre à jour
        if (selectedItem?.isLocked || selectedItem?.isGalleryFolder) {
            toast.error(t('folders:ceDossierNePeutPasEtreModifie'));
            setIsEditPanelOpen(false);
            setSelectedItem(null);
            return;
        }

        setIsLoading(true);
        setIsEditSubmitting(true);
        setErrorMessage('');

        const isSubfolderItem = selectedItem?.type === 'intervention' || selectedItem?.type === 'procedure_report';

        const apiCall = isSubfolderItem
            ? horodatyApi.updateIntervention(currentPath?.[0], itemData.id, {
                  name: itemData.name || undefined,
                  user_uniq_id: itemData.user || undefined,
                  status: itemData.status || null,
                  mandatory_procedures: itemData.procedures ?? undefined,
              })
            : horodatyApi.updateFolder(itemData.id, {
                  name: itemData.name,
                  reference: itemData.reference || undefined,
                  external_ref: itemData.reference || undefined,
                  notes: itemData.comments || undefined,
                  street_1: itemData.address || undefined,
                  'user-to-affect': itemData.user || undefined,
                  status: itemData.status || null,
              });

        apiCall
            .then(() => {
                if (isSubfolderItem) {
                    loadedFoldersRef.current.clear();
                    setRefreshKey((k) => k + 1);
                } else {
                    refreshRoot();
                }
                toast.success(isSubfolderItem ? t('folders:sousDossierMisAJour') : t('folders:dossierMisAJour'));
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('folders:impossibleDeMettreAJour');
                setErrorMessage(message);
                toast.error(message);
            })
            .finally(() => {
                setIsEditPanelOpen(false);
                setSelectedItem(null);
                setIsLoading(false);
                setIsEditSubmitting(false);
            });
    };

    const handleEditItem = (item) => {
        // Bloquer l'édition si le dossier est verrouillé ou est une galerie
        if (item.isLocked || item.isGalleryFolder) {
            toast.error(t('folders:ceDossierNePeutPasEtreModifie'));
            return;
        }
        setSelectedItem(item);
        setIsEditPanelOpen(true);
    };

    const handleMoveItem = (item) => {
        const canMoveCurrentItem =
            item?.type === 'procedure_report'
                ? can('MOVE_PROCEDURE_REPORTS')
                : item?.type === 'photo'
                  ? can('MOVE_CERTIFIED_PHOTOS')
                  : can('MOVE_FOLDERS');
        if (!canMoveCurrentItem) {
            toast.error(t('folders:vousNAvezPasLesDroitsNecessairesPour'));
            return;
        }

        if (item.isLocked || item.isGalleryFolder) return;
        setItemToMove(item);
        setIsMovePanelOpen(true);
    };

    const canMoveItem = useCallback(
        (item) => {
            if (!item) return false;
            if (item.type === 'procedure_report') return can('MOVE_PROCEDURE_REPORTS');
            if (item.type === 'photo') return can('MOVE_CERTIFIED_PHOTOS');
            return can('MOVE_FOLDERS');
        },
        [can],
    );

    const handleMoveSubmit = ({ directoryId, destination_uniq_id, destination_type, destination_folder_uniq_id }) => {
        if (!itemToMove) return;

        setIsMoveSubmitting(true);
        const moveRequest =
            itemToMove.type === 'photo'
                ? (() => {
                      const sourceFolderId = resolvedHierarchy.rootFolderId ?? currentFolderId;
                      const sourceInterventionId = resolvedHierarchy.isSubfolder
                          ? currentFolderId
                          : (primaryInterventionByFolderId[currentFolderId] ?? null);

                      if (!sourceInterventionId) {
                          throw new Error('Sous-dossier source introuvable pour la photo.');
                      }

                      const targetFolderUniqId =
                          destination_type === 'folder'
                              ? destination_uniq_id
                              : destination_folder_uniq_id || destination_uniq_id;

                      const payload =
                          destination_type === 'subfolder'
                              ? { folder: targetFolderUniqId, intervention: destination_uniq_id }
                              : { folder: targetFolderUniqId };

                      return horodatyApi.moveInterventionPicture(
                          sourceFolderId,
                          sourceInterventionId,
                          directoryId,
                          payload,
                      );
                  })()
                : horodatyApi.moveDirectory(directoryId, { destination_uniq_id, destination_type });

        Promise.resolve(moveRequest)
            .then(() => {
                loadedFoldersRef.current.clear();
                if (currentFolderId === 'root') {
                    refreshRoot();
                } else {
                    setRefreshKey((k) => k + 1);
                }
                toast.success(
                    itemToMove.type === 'photo' ? t('folders:photoDeplaceeAvecSucces') : t('folders:dossierDeplaceAvecSucces'),
                );
            })
            .catch((error) => {
                const defaultMessage =
                    itemToMove?.type === 'photo'
                        ? t('folders:impossibleDeDeplacerLaPhoto')
                        : t('folders:impossibleDeDeplacerLeDossier');
                const message = error?.response?.data?.message || error?.response?.data?.error || defaultMessage;
                toast.error(message);
            })
            .finally(() => {
                setIsMovePanelOpen(false);
                setItemToMove(null);
                setIsMoveSubmitting(false);
            });
    };

    const handleArchiveFolder = (item) => {
        if (!item?.id) return;
        horodatyApi
            .archiveFolder(item.id)
            .then(() => {
                toast.success(t('folders:dossierArchive'));
                refreshRoot();
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('folders:impossibleDArchiverLeDossier');
                toast.error(message);
            });
    };

    const handleRestoreFolder = (item) => {
        if (!item?.id) return;
        horodatyApi
            .restoreFolder(item.id)
            .then(() => {
                toast.success(t('folders:dossierRestaure'));
                refreshArchived();
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('folders:impossibleDeRestaurerLeDossier');
                toast.error(message);
            });
    };

    // Archive/restaure un sous-dossier (intervention)
    const handleArchiveSubfolder = (item) => {
        if (!item?.id || !resolvedHierarchy.rootFolderId) return;
        horodatyApi
            .archiveSubfolder(resolvedHierarchy.rootFolderId, item.id)
            .then(() => {
                toast.success(t('folders:sousDossierArchive'));
                // Forcer le rechargement du dossier courant (invalider les deux modes)
                loadedFoldersRef.current.delete(`${currentFolderId}::normal`);
                loadedFoldersRef.current.delete(`${currentFolderId}::archived`);
                setRefreshKey((k) => k + 1);
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('folders:impossibleDArchiverLeSousDossier');
                toast.error(message);
            });
    };

    const handleRestoreSubfolder = (item) => {
        if (!item?.id || !resolvedHierarchy.rootFolderId) return;
        horodatyApi
            .restoreSubfolder(resolvedHierarchy.rootFolderId, item.id)
            .then(() => {
                toast.success(t('folders:sousDossierRestaure'));
                // Forcer le rechargement du dossier courant (invalider les deux modes)
                loadedFoldersRef.current.delete(`${currentFolderId}::normal`);
                loadedFoldersRef.current.delete(`${currentFolderId}::archived`);
                setRefreshKey((k) => k + 1);
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('folders:impossibleDeRestaurerLeSousDossier');
                toast.error(message);
            });
    };

    // Handler unifié pour archive/restore depuis le dialogue de confirmation
    const handleConfirmFolderAction = (actionType, item) => {
        // L'item est un dossier racine si:
        // - il vient du header à depth=1 (currentFolderItem, type=folder)
        // - il a un type explicite 'folder' (depuis le tableau au root)
        const isRootFolder = item?.type === 'folder' || (depth === 1 && !item?.type);

        if (actionType === 'archive') {
            if (isRootFolder) {
                handleArchiveFolder(item);
            } else {
                handleArchiveSubfolder(item);
            }
        } else {
            if (isRootFolder) {
                handleRestoreFolder(item);
            } else {
                handleRestoreSubfolder(item);
            }
        }
    };

    const handleApplyFilters = (filterData) => {
        console.log('[FoldersExplorer] handleApplyFilters', { filterData, currentFolderId });
        setActiveFilters(filterData);
        setIsFilterPanelOpen(false);
        // Invalider le cache pour forcer le rechargement
        loadedFoldersRef.current.clear();

        if (currentFolderId === 'root') {
            // Au root : appeler directement avec les nouveaux filtres (pas besoin de refreshKey)
            refreshRoot(filterData);
        } else if (currentFolderId === 'archived') {
            refreshArchived(filterData);
        } else {
            // Dans un dossier/sous-dossier : forcer le rechargement via le useEffect
            setRefreshKey((k) => k + 1);
        }
    };

    const handleCloseCreatePanel = () => {
        setIsCreatePanelOpen(false);
        setResetKey((prev) => prev + 1);
    };

    const handleCloseEditPanel = () => {
        setIsEditPanelOpen(false);
        setSelectedItem(null);
    };

    const handleCloseFilterPanel = () => {
        setIsFilterPanelOpen(false);
    };

    const handleAddPhoto = () => {
        // Ouvrir le sheet de capture photo si on est dans un dossier
        if (currentFolderId) {
            setIsPhotoCaptureOpen(true);
        } else {
            // Si on est à la racine, afficher l'app mobile
            setIsMobileAppModalOpen(true);
        }
    };

    const handlePhotoCapture = async ({ mediaType = 'photo', base64, blob, fileName, mimeType, durationMs }) => {
        if (!currentFolderId || currentPath.length === 0) {
            toast.error(t('folders:veuillezSelectionnerUnDossier'));
            return;
        }

        // Le folderId est toujours le premier élément du chemin (dossier racine)
        const folderId = currentPath[0];
        // L'intervention est le dernier élément du chemin (sous-dossier actuel)
        // Si on est à la racine d'un dossier (depth=1), utiliser primaryInterventionByFolderId
        const interventionId =
            currentPath.length >= 2 ? currentPath[currentPath.length - 1] : primaryInterventionByFolderId[folderId];

        if (!interventionId) {
            toast.error(t('folders:aucuneInterventionTrouveePourCeDossier'));
            return;
        }

        setIsPhotoUploading(true);

        try {
            // Obtenir la géolocalisation
            let latitude = null;
            let longitude = null;

            try {
                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 0,
                    });
                });
                latitude = position.coords.latitude;
                longitude = position.coords.longitude;
            } catch (geoError) {
                console.warn('Géolocalisation non disponible:', geoError);
                // Continue sans géolocalisation
            }

            const isVideo = mediaType === 'video';
            await horodatyApi.uploadInterventionPicture(folderId, interventionId, {
                file_name: fileName,
                latitude,
                longitude,
                ...(isVideo ? { video: blob, mime_type: mimeType, duration_ms: durationMs } : { base64 }),
            });

            toast.success(isVideo ? t('folders:videoAjouteeAvecSucces') : t('folders:photoAjouteeAvecSucces'));
            setIsPhotoCaptureOpen(false);

            // Rafraîchir les données du dossier en forçant un re-render
            setRefreshKey((k) => k + 1);
        } catch (error) {
            console.error('Erreur upload média:', error);
            const message = error?.response?.data?.message || t('folders:impossibleDAjouterLeMedia');
            toast.error(message);
        } finally {
            setIsPhotoUploading(false);
        }
    };

    const handleAddProcedure = () => {
        setIsProcedurePanelOpen(true);
    };

    const handleProcedureSelected = (templateUniqId) => {
        // Navigation vers la page de création avec le dossier/sous-dossier courant
        const state = { folderId: resolvedHierarchy.rootFolderId || currentFolderId };

        // Si on est dans un sous-dossier, passer aussi le parentSubfolderId
        if (resolvedHierarchy.isSubfolder && currentFolderId !== 'root') {
            state.parentSubfolderId = currentFolderId;
        }

        navigate(`/procedures/${templateUniqId}/reports/create`, { state });
    };

    return (
        <div className="space-y-6">
            {/* Page Header with Breadcrumb */}
            <PageHeader
                customLayout={true}
                actions={
                    <>
                        {depth === 0 && (
                            <Button
                                variant="link"
                                color="secondary"
                                onClick={() => navigate(currentId === 'archived' ? '/folders' : '/folders/archived')}
                            >
                                {currentId === 'archived' ? t('folders:dossiers') : t('folders:archives')}
                            </Button>
                        )}
                        {depth >= 1 &&
                            currentDirectoryItem &&
                            !currentDirectoryItem.isLocked &&
                            !currentDirectoryItem.isGalleryFolder &&
                            can('ARCHIVE_FOLDERS') && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="plain"
                                            color="neutral"
                                            size="auto"
                                            className="w-10 h-10 flex items-center justify-center bg-surface-hover hover:bg-surface-active transition-colors"
                                        >
                                            <MoreVertical className="w-5 h-5 text-foreground" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        {!currentDirectoryItem.isArchived &&
                                            !(depth === 1 && currentFolderItem?.raw?.is_archived) && (
                                                <DropdownMenuItem
                                                    onClick={() =>
                                                        setFolderActionDialog({
                                                            type: 'archive',
                                                            item: currentDirectoryItem,
                                                        })
                                                    }
                                                >
                                                    <FolderArchive className="w-4 h-4" />
                                                    {t('folders:archiver')}
                                                </DropdownMenuItem>
                                            )}
                                        {(currentDirectoryItem.isArchived ||
                                            (depth === 1 && currentFolderItem?.raw?.is_archived)) && (
                                            <DropdownMenuItem
                                                onClick={() =>
                                                    setFolderActionDialog({
                                                        type: 'restore',
                                                        item: currentDirectoryItem,
                                                    })
                                                }
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                                {t('folders:restaurer')}
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                    </>
                }
            >
                <div className="flex items-center gap-2">
                    {depth > 0 && (
                        <BackButton
                            onClick={() => {
                                if (location.state?.from) {
                                    navigate(location.state.from);
                                } else {
                                    handleBreadcrumbNavigate(breadcrumbItems.length - 2);
                                }
                            }}
                        />
                    )}
                    <FoldersBreadcrumb
                        items={breadcrumbItems}
                        onNavigate={handleBreadcrumbNavigate}
                        rootLabel={isArchiveRoute ? t('folders:dossiersArchives') : t('folders:dossiers')}
                    />
                </div>
            </PageHeader>

            {/* Actions Bar */}
            <div className="px-8">
                <FoldersActionBar
                    depth={depth}
                    onFilter={() => setIsFilterPanelOpen(true)}
                    activeFilterCount={(() => {
                        if (!activeFilters) return 0;
                        let count = 0;
                        if (activeFilters.period && activeFilters.period !== 'all') count++;
                        if (activeFilters.collaborator && activeFilters.collaborator !== 'all') count++;
                        if (activeFilters.subsidiaries) count++;
                        return count;
                    })()}
                    onCreate={
                        can('CREATE_FOLDERS') && !isInsideGalleryIntervention
                            ? () => setIsCreatePanelOpen(true)
                            : undefined
                    }
                    onAddPhoto={can('CAPTURE_CERTIFIED_PHOTOS') ? handleAddPhoto : undefined}
                    onAddProcedure={can('CREATE_PROCEDURES') && !isIndividual ? handleAddProcedure : undefined}
                    mandatoryProceduresCount={
                        currentMandatoryProcedures.length > 0
                            ? `${currentItems.filter((c) => c.type === 'procedure_report' && c.status === 'completed' && currentMandatoryProcedures.some((mp) => mp.uniq_id === c.procedureTemplateId)).length}/${currentMandatoryProcedures.length}`
                            : undefined
                    }
                    onMandatoryProcedures={
                        currentMandatoryProcedures.length > 0
                            ? () => setIsMandatoryProceduresPanelOpen(true)
                            : undefined
                    }
                    onExportReports={
                        !isArchiveRoute && isOwnerOrAdmin() ? () => setIsExportReportsOpen(true) : undefined
                    }
                />
            </div>

            {/* Items Table */}
            {isLoading && currentItems.length === 0 ? (
                <div className="px-8">
                    <div className="bg-card rounded-lg border border-border overflow-hidden">
                        <div className="p-4 space-y-3">
                            {Array.from({ length: 6 }).map((_, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="w-10 h-10 rounded-md" />
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-56" />
                                            <Skeleton className="h-3 w-24" />
                                        </div>
                                    </div>
                                    <Skeleton className="h-8 w-8 rounded-md" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <div className="px-8">
                        <FoldersTable
                            items={filteredItems}
                            depth={depth}
                            isSuperAdmin={isSuperAdmin}
                            adminTemplateMap={adminTemplateMap}
                            sorting={sorting}
                            onSortingChange={(newSorting) => {
                                setSorting(newSorting);
                                if (currentFolderId === 'root') {
                                    refreshRoot(activeFilters, newSorting);
                                } else if (currentFolderId === 'archived') {
                                    refreshArchived(activeFilters, newSorting);
                                }
                            }}
                            manualSorting={currentFolderId === 'root' || currentFolderId === 'archived'}
                            onNavigate={handleNavigateToFolder}
                            onOpenPhoto={handleOpenPhoto}
                            onDownloadPhoto={handleDownloadPhoto}
                            onViewCertificate={handleViewCertificate}
                            onArchivePhoto={
                                /* can('ARCHIVE_CERTIFIED_PHOTOS') */ isOwnerOrAdmin() ? handleArchivePhoto : undefined
                            }
                            onArchiveItem={
                                can('ARCHIVE_FOLDERS')
                                    ? (item) => setFolderActionDialog({ type: 'archive', item })
                                    : undefined
                            }
                            onRestoreItem={
                                can('ARCHIVE_FOLDERS')
                                    ? (item) => setFolderActionDialog({ type: 'restore', item })
                                    : undefined
                            }
                            onEdit={can('UPDATE_FOLDERS') ? handleEditItem : undefined}
                            onMove={
                                can('MOVE_FOLDERS') || can('MOVE_PROCEDURE_REPORTS') || can('MOVE_CERTIFIED_PHOTOS')
                                    ? handleMoveItem
                                    : undefined
                            }
                            canMoveItem={canMoveItem}
                            users={collaboratorOptions.filter((c) => c.id !== 'all')}
                            isLoading={isLoading}
                            isLoadingPhotos={isLoadingPhotos}
                            onStatusChange={
                                can('UPDATE_FOLDERS')
                                    ? (item, newStatus) => {
                                          if (!item?.id) return;

                                          horodatyApi
                                              .updateDirectoryStatus(item.id, newStatus)
                                              .then(() => {
                                                  // Mettre à jour le status localement pour un feedback immédiat
                                                  setItemsByFolderId((prev) => {
                                                      const key = currentFolderId;
                                                      const items = prev[key];
                                                      if (!items) return prev;
                                                      return {
                                                          ...prev,
                                                          [key]: items.map((it) =>
                                                              it.id === item.id ? { ...it, status: newStatus } : it,
                                                          ),
                                                      };
                                                  });
                                                  toast.success(t('folders:statutMisAJour'));
                                              })
                                              .catch((error) => {
                                                  const message =
                                                      error?.response?.data?.message ||
                                                      t('folders:impossibleDeMettreAJourLeStatut');
                                                  toast.error(message);
                                              });
                                      }
                                    : undefined
                            }
                            assigningFolderId={assigningFolderId}
                            onAssignUser={
                                can('UPDATE_FOLDERS')
                                    ? (folderId, userUniqId) => {
                                          // Inline assignment: PATCH /api/folders/{id} with user-to-affect
                                          if (!folderId) return;
                                          setAssigningFolderId(folderId);
                                          setErrorMessage('');

                                          const currentName =
                                              itemsByFolderId?.root?.find((it) => String(it.id) === String(folderId))
                                                  ?.name || t('folders:dossier');

                                          horodatyApi
                                              .updateFolder(folderId, {
                                                  // Name is required in ROUTES.md for update; reuse current name if available.
                                                  name: currentName,
                                                  'user-to-affect': userUniqId || undefined,
                                              })
                                              .then(() => {
                                                  refreshRoot();
                                                  toast.success(t('folders:dossierAssigne'));
                                              })
                                              .catch((error) => {
                                                  const message =
                                                      error?.response?.data?.message ||
                                                      t('folders:impossibleDAssignerLeDossier');
                                                  setErrorMessage(message);
                                                  toast.error(message);
                                              })
                                              .finally(() => setAssigningFolderId(null));
                                      }
                                    : undefined
                            }
                        />
                    </div>
                </div>
            )}

            {/* Sheets */}
            <SheetFolderCreate
                open={isCreatePanelOpen}
                onClose={handleCloseCreatePanel}
                onSubmit={handleCreateFolder}
                resetKey={resetKey}
                isSubfolder={depth > 0}
                users={can('UPDATE_FOLDER_MANAGERS') ? collaborators : []}
                isSubmitting={isCreateSubmitting}
            />

            <SheetFolderEdit
                open={isEditPanelOpen}
                onClose={handleCloseEditPanel}
                onSubmit={handleUpdateItem}
                item={selectedItem}
                itemType={selectedItem?.type || 'folder'}
                users={can('UPDATE_FOLDER_MANAGERS') ? collaborators : []}
                isSubmitting={isEditSubmitting}
            />

            <SheetFolderMove
                open={isMovePanelOpen}
                onClose={() => {
                    setIsMovePanelOpen(false);
                    setItemToMove(null);
                }}
                onSubmit={handleMoveSubmit}
                item={itemToMove}
                isSubmitting={isMoveSubmitting}
            />

            <SheetFolderFilter
                open={isFilterPanelOpen}
                onClose={handleCloseFilterPanel}
                onSubmit={handleApplyFilters}
                filters={activeFilters}
                collaborators={collaboratorOptions}
                subsidiaries={subsidiaries}
            />

            <DialogMobileApp open={isMobileAppModalOpen} onOpenChange={setIsMobileAppModalOpen} />

            <DialogExportReports open={isExportReportsOpen} onClose={() => setIsExportReportsOpen(false)} />

            <SheetPhotoCapture
                open={isPhotoCaptureOpen}
                onClose={() => setIsPhotoCaptureOpen(false)}
                onCapture={handlePhotoCapture}
                isUploading={isPhotoUploading}
            />

            <SheetSelectProcedure
                open={isProcedurePanelOpen}
                onClose={() => setIsProcedurePanelOpen(false)}
                onSelect={handleProcedureSelected}
            />

            <SheetMandatoryProcedures
                open={isMandatoryProceduresPanelOpen}
                onClose={() => setIsMandatoryProceduresPanelOpen(false)}
                mandatoryProcedures={currentMandatoryProcedures}
                children={currentItems}
                onNavigate={(child) => {
                    setIsMandatoryProceduresPanelOpen(false);
                    const returnTo = folderUrl(currentFolderId);
                    setReportReturnTo(returnTo);
                    const basePath = `/procedures/${child.procedureTemplateId}/reports/${child.id}`;
                    const path = child.status === 'draft' ? `${basePath}/edit` : basePath;
                    navigate(path, { state: { returnTo } });
                }}
                onCreate={(templateUniqId) => {
                    setIsMandatoryProceduresPanelOpen(false);
                    setReportReturnTo(folderUrl(currentFolderId));
                    handleProcedureSelected(templateUniqId);
                }}
            />

            <AlertDialog
                open={folderActionDialog !== null}
                onOpenChange={(open) => {
                    if (!open) setFolderActionDialog(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {(() => {
                                const isFolder =
                                    folderActionDialog?.item?.type === 'folder' ||
                                    (depth === 1 && !folderActionDialog?.item?.type);
                                const label = isFolder ? t('folders:leDossier') : t('folders:leSousDossier');
                                return folderActionDialog?.type === 'archive'
                                    ? `Archiver ${label}`
                                    : `Restaurer ${label}`;
                            })()}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {(() => {
                                const isFolder =
                                    folderActionDialog?.item?.type === 'folder' ||
                                    (depth === 1 && !folderActionDialog?.item?.type);
                                const label = isFolder ? t('folders:leDossier') : t('folders:leSousDossier');
                                const name = folderActionDialog?.item?.name ?? '';
                                if (folderActionDialog?.type === 'archive') {
                                    return `Voulez-vous archiver ${label} « ${name} » ?${isFolder ? t('folders:ilNeSeraPlusVisibleDansLaListe') : t('folders:tousSesEnfantsSerontEgalementArchives')}`;
                                }
                                return `Voulez-vous restaurer ${label} « ${name} » ?${isFolder ? t('folders:ilRedeviendraVisibleDansLaListePrincipale') : t('folders:tousSesEnfantsSerontEgalementRestaures')}`;
                            })()}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('folders:annuler')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                handleConfirmFolderAction(folderActionDialog?.type, folderActionDialog?.item);
                                setFolderActionDialog(null);
                            }}
                        >
                            {folderActionDialog?.type === 'archive' ? t('folders:archiver') : t('folders:restaurer')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
